export interface DescriptionRun { text: string; bold: boolean; italic: boolean }
export type DescriptionBlock =
  | { type: "text" | "heading"; level: number; runs: DescriptionRun[] }
  | { type: "list"; ordered: boolean; start: number; items: DescriptionRun[][] }
  | { type: "blank" };

/** Small, shared Markdown subset for pasted/AI descriptions. No HTML is executed. */
export function descriptionRuns(text: string, bold = false, italic = false): DescriptionRun[] {
  const runs: DescriptionRun[] = [];
  const append = (value: string) => {
    if (!value) return;
    const last = runs[runs.length - 1];
    if (last && last.bold === bold && last.italic === italic) last.text += value;
    else runs.push({ text: value, bold, italic });
  };
  let plain = "";
  for (let i = 0; i < text.length;) {
    if (text[i] === "\\" && /[\\*_#]/.test(text[i + 1] ?? "")) {
      plain += text[i + 1]; i += 2; continue;
    }
    const marker = text.startsWith("**", i) ? "**" : text.startsWith("__", i) ? "__" :
      text[i] === "*" || text[i] === "_" ? text[i] : "";
    // Underscores within identifiers (e.g. HS_2026_A) remain literal.
    if (marker && !(marker[0] === "_" && /\w/.test(text[i - 1] ?? "")) &&
      text[i + marker.length] && !/\s/.test(text[i + marker.length])) {
      let end = text.indexOf(marker, i + marker.length);
      while (end !== -1 && (text[end - 1] === "\\" || /\s/.test(text[end - 1]))) {
        end = text.indexOf(marker, end + marker.length);
      }
      if (end > i + marker.length) {
        append(plain); plain = "";
        runs.push(...descriptionRuns(text.slice(i + marker.length, end),
          bold || marker.length === 2, italic || marker.length === 1));
        i = end + marker.length; continue;
      }
    }
    plain += text[i++];
  }
  append(plain);
  return runs;
}

export function parseDescription(text: string): DescriptionBlock[] {
  const blocks: DescriptionBlock[] = [];
  for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
    if (!line.trim()) { blocks.push({ type: "blank" }); continue; }
    const heading = line.match(/^ {0,3}(#{1,6})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length,
        runs: descriptionRuns(heading[2].replace(/\s+#+\s*$/, "")) });
      continue;
    }
    const list = line.match(/^\s*(?:([-+*•])|(\d+)[.)])\s+(.+)$/);
    if (list) {
      const ordered = Boolean(list[2]);
      const last = blocks[blocks.length - 1];
      if (last?.type === "list" && last.ordered === ordered) last.items.push(descriptionRuns(list[3]));
      else blocks.push({ type: "list", ordered, start: Number(list[2] ?? 1), items: [descriptionRuns(list[3])] });
      continue;
    }
    blocks.push({ type: "text", level: 0, runs: descriptionRuns(line) });
  }
  return blocks;
}
